/**
 * Equipment — the return report flow (migration 29) and its archive.
 *
 *   admin: ReturnModal → equipment_issue_return_report  (COLLECTED → RETURN_PENDING)
 *   holder: ReturnReportPanel → confirm / dispute
 *   admin:  amend (re-issue) / close without confirmation
 *   on close: the PDF with both acceptances is rendered in the browser
 *             (equipmentReportPdf.ts) and stored on the booking
 *   ArchiveTab: download every stored PDF as one ZIP, then remove the
 *             files from storage (rows stay as tombstones)
 */
import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { isDemoMode } from "../state/store";
import { displayName } from "../utils/names";
import { restRpc, restUpsert, restPatch, storageUpload, storageDownload, storageDelete } from "../utils/withRetry";
import { formatBytes } from "../utils/imageCompress";
import { toast } from "../utils/toast";
import {
  reportOutcome,
  returnReportFileName,
  type EquipmentBooking,
  type EquipmentBookingLine,
  type EquipmentItem,
  type EquipmentReturnReport,
  type EquipmentDocument,
} from "../utils/equipment";
import { PersonSelect, backdropHandlers, fmtStamp, saveBytes, openBytes, nowIso } from "./equipmentShared";

/* ─────────────────── PDF helpers ─────────────────────────────────── */

export async function renderReportPdf(booking: EquipmentBooking, report: EquipmentReturnReport): Promise<Uint8Array> {
  const { buildReturnReportPdf } = await import("./equipmentReportPdf");
  return buildReturnReportPdf({ booking, report });
}

/** Render the final report and store it on the booking as a
 *  'return_report' document. Throws on failure (the caller decides how
 *  loud to be — the register row is already closed at that point). */
export async function storeReturnReportPdf(
  booking: EquipmentBooking,
  report: EquipmentReturnReport,
  uploadedBy: string,
): Promise<number> {
  const bytes = await renderReportPdf(booking, report);
  const name = returnReportFileName(booking, report);
  const path = `bookings/${booking.id}/${Date.now()}-return_report-${name}`;
  await storageUpload("equipment-docs", path, new Blob([bytes.slice().buffer as ArrayBuffer], { type: "application/pdf" }));
  await restUpsert("equipment_booking_documents", [{
    booking_id: booking.id,
    kind: "return_report",
    path,
    file_name: name,
    content_type: "application/pdf",
    size_bytes: bytes.byteLength,
    uploaded_by: uploadedBy,
  }]);
  return bytes.byteLength;
}

/* ─────────────────── report panel (holder + admin views) ─────────── */

export function ReturnReportPanel({
  booking, report: r, me, profileId, isAdmin, onChanged, onAmend,
}: {
  booking: EquipmentBooking;
  report: EquipmentReturnReport;
  me: string;
  profileId: string | null;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
  /** Admin: reopen the form prefilled with this report. */
  onAmend?: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const demo = isDemoMode.value;
  const isHolder = demo
    ? booking.requester_name === me || booking.on_behalf_of === me
    : !!profileId && booking.requester_id === profileId;
  const open = booking.status === "RETURN_PENDING";
  const holder = r.returned_by || booking.on_behalf_of || booking.requester_name;
  const outcome = reportOutcome(r.lines);

  const preview = async (): Promise<void> => {
    try {
      const bytes = await renderReportPdf(booking, r);
      if (!openBytes(bytes, "application/pdf")) saveBytes(bytes, returnReportFileName(booking, r), "application/pdf");
    } catch (err) {
      toast(err instanceof Error ? `Couldn't render the PDF — ${err.message}` : "Couldn't render the PDF");
    }
  };

  const confirmIt = async (): Promise<void> => {
    if (demo) { toast("Demo mode — the confirmation isn't saved."); return; }
    if (!confirm(`Confirm the return report for ${booking.reference} as declared?\n\nThis closes the booking. The PDF with both acceptances is stored on it.`)) return;
    setBusy(true);
    try {
      await restRpc("equipment_confirm_return_report", { p_booking_id: booking.id });
      const done: EquipmentReturnReport = { ...r, status: "CONFIRMED", confirmed_by: me, confirmed_at: nowIso(), closed_without_confirmation: false };
      try {
        await storeReturnReportPdf({ ...booking, status: "RETURNED" }, done, displayName(me));
        toast(`${booking.reference} closed — report confirmed and PDF stored`);
      } catch (err) {
        toast(`${booking.reference} closed. The PDF couldn't be stored (${err instanceof Error ? err.message : "error"}) — an admin can generate it from Manage → Archive.`);
      }
      await onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Confirmation failed");
    } finally {
      setBusy(false);
    }
  };

  const dispute = async (): Promise<void> => {
    if (demo) { toast("Demo mode — the dispute isn't saved."); return; }
    const note = prompt("What is wrong with the report? (sent to the equipment admins)") ?? "";
    if (!note.trim()) return;
    setBusy(true);
    try {
      await restRpc("equipment_dispute_return_report", { p_booking_id: booking.id, p_note: note.trim() });
      toast("Report disputed — the admins have been notified and will re-issue it");
      await onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't dispute the report");
    } finally {
      setBusy(false);
    }
  };

  const closeWithout = async (): Promise<void> => {
    if (demo) { toast("Demo mode — nothing is closed."); return; }
    const note = prompt(`Close ${booking.reference} without ${displayName(holder)}'s confirmation.\n\nNote (required — recorded on the report):`) ?? "";
    if (!note.trim()) { toast("A note is required."); return; }
    setBusy(true);
    try {
      await restRpc("equipment_close_return_without_confirmation", { p_booking_id: booking.id, p_note: note.trim() });
      const done: EquipmentReturnReport = { ...r, status: "CONFIRMED", confirmed_by: me, confirmed_at: nowIso(), closed_without_confirmation: true, close_note: note.trim() };
      try {
        await storeReturnReportPdf({ ...booking, status: "RETURNED" }, done, displayName(me));
        toast(`${booking.reference} closed without confirmation — PDF stored`);
      } catch (err) {
        toast(`${booking.reference} closed. The PDF couldn't be stored (${err instanceof Error ? err.message : "error"}) — generate it from Manage → Archive.`);
      }
      await onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't close the return");
    } finally {
      setBusy(false);
    }
  };

  let stateLine: JSX.Element;
  if (r.status === "CONFIRMED" && r.closed_without_confirmation) {
    stateLine = <span class="eq-report-state warn">Closed by {displayName(r.confirmed_by)} without the holder's confirmation · {fmtStamp(r.confirmed_at)}{r.close_note && ` · “${r.close_note}”`}</span>;
  } else if (r.status === "CONFIRMED") {
    stateLine = <span class="eq-report-state ok">✓ Confirmed by {displayName(r.confirmed_by)} · {fmtStamp(r.confirmed_at)}</span>;
  } else if (r.status === "DISPUTED") {
    stateLine = <span class="eq-report-state bad">⚠ Disputed by {displayName(holder)} · {fmtStamp(r.disputed_at)} · “{r.dispute_note}” — {isAdmin ? "amend and re-issue the report" : "waiting for the admin to amend it"}</span>;
  } else if (isHolder && open) {
    stateLine = <span class="eq-report-state">Please check what was declared and <b>confirm</b> it — or dispute it if something is wrong.</span>;
  } else {
    stateLine = <span class="eq-report-state">Awaiting {displayName(holder)}'s confirmation since {fmtStamp(r.declared_at)}.</span>;
  }

  return (
    <div class="eq-report">
      <div class="eq-report-head">
        <b>📄 Return report v{r.version}</b>
        <span class={`eq-outcome ${outcome === "Incomplete" ? "bad" : outcome.includes("damage") ? "warn" : "ok"}`}>{outcome}</span>
        <span class="eq-report-meta">
          declared by {displayName(r.declared_by || r.received_by)} · {fmtStamp(r.declared_at)} · returned by {displayName(r.returned_by)}
        </span>
      </div>
      <div class="eq-scroll">
        <table class="eq-table eq-report-table" aria-label="Return report lines">
          <thead>
            <tr><th>Item</th><th class="num">Out</th><th class="num">Returned</th><th class="num">Lost</th><th class="num">Damaged</th><th>Notes</th></tr>
          </thead>
          <tbody>
            {r.lines.map((l) => (
              <tr key={l.line_id}>
                <td><b>{l.item_name}</b></td>
                <td class="num">{l.due}</td>
                <td class="num">{l.returned}</td>
                <td class={`num${l.lost ? " bad" : ""}`}>{l.lost || ""}</td>
                <td class={`num${l.damaged ? " warn" : ""}`}>{l.damaged || ""}</td>
                <td class="eq-notes-cell">{l.damage_note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {r.admin_notes && <div class="eq-report-notes">Notes: {r.admin_notes}</div>}
      {stateLine}
      <div class="eq-booking-actions">
        <button type="button" class="eq-action eq-action-quiet" disabled={busy} onClick={() => void preview()}>Preview PDF</button>
        {open && isHolder && (
          <>
            <button type="button" class="eq-action" disabled={busy} onClick={() => void confirmIt()}>
              {busy ? "Working…" : "Confirm and close"}
            </button>
            {r.status !== "DISPUTED" && (
              <button type="button" class="eq-action eq-action-danger" disabled={busy} onClick={() => void dispute()}>Dispute…</button>
            )}
          </>
        )}
        {open && isAdmin && (
          <>
            {onAmend && <button type="button" class="eq-action" disabled={busy} onClick={onAmend}>Amend report…</button>}
            <button type="button" class="eq-action eq-action-danger" disabled={busy} onClick={() => void closeWithout()}>Close without confirmation…</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────── admin: issue / amend the report ─────────────── */

interface ReturnLineState {
  returned: number;
  lost: number;
  damaged: number;
  note: string;
}

export function ReturnModal({
  booking, items, lines, report, me, onClose, onIssued,
}: {
  booking: EquipmentBooking;
  items: EquipmentItem[];
  lines: EquipmentBookingLine[];
  /** Existing report when amending after a dispute (prefills the form). */
  report: EquipmentReturnReport | null;
  me: string;
  onClose: () => void;
  onIssued: () => Promise<void>;
}): JSX.Element {
  const [st, setSt] = useState<Record<number, ReturnLineState>>(() => {
    const m: Record<number, ReturnLineState> = {};
    lines.forEach((l) => {
      const prev = report?.lines.find((x) => x.line_id === l.id);
      m[l.id] = prev
        ? { returned: prev.returned, lost: prev.lost, damaged: prev.damaged, note: prev.damage_note }
        : { returned: l.qty_approved ?? l.qty_requested, lost: 0, damaged: 0, note: "" };
    });
    return m;
  });
  const [returnedBy, setReturnedBy] = useState(report?.returned_by || booking.on_behalf_of || booking.requester_name);
  const [supervisor, setSupervisor] = useState(report?.received_by || me);
  const [notes, setNotes] = useState(report?.admin_notes ?? "");
  const [busy, setBusy] = useState(false);

  const nameOf = (l: EquipmentBookingLine): string => items.find((i) => i.id === l.item_id)?.name ?? "?";
  const upd = (lineId: number, patch: Partial<ReturnLineState>): void =>
    setSt((m) => {
      const cur = m[lineId];
      if (!cur) return m;
      return { ...m, [lineId]: { ...cur, ...patch } };
    });

  const problems: string[] = [];
  lines.forEach((l) => {
    const v = st[l.id];
    if (!v) return;
    const due = l.qty_approved ?? l.qty_requested;
    if (v.returned + v.lost !== due) problems.push(`${nameOf(l)}: returned (${v.returned}) + lost (${v.lost}) must equal ${due}`);
    if (v.damaged > v.returned) problems.push(`${nameOf(l)}: damaged can't exceed returned`);
    if (v.damaged > 0 && !v.note.trim()) problems.push(`${nameOf(l)}: describe the damage`);
  });

  const save = async (): Promise<void> => {
    if (problems.length > 0) { toast(problems[0] ?? "Check the quantities"); return; }
    if (!supervisor) { toast("Choose the General Affairs colleague receiving the kit."); return; }
    if (!returnedBy) { toast("Choose who is returning."); return; }
    if (isDemoMode.value) { toast("Demo mode — the report isn't issued."); onClose(); return; }
    setBusy(true);
    try {
      await restRpc("equipment_issue_return_report", {
        p_booking_id: booking.id,
        p_lines: lines.map((l) => {
          const v = st[l.id];
          // out_of_service stays in the payload for the RPC's schema; since
          // migration 31 every damaged unit is set aside, so it is always 0.
          return { line_id: l.id, returned: v?.returned ?? 0, lost: v?.lost ?? 0, damaged: v?.damaged ?? 0, out_of_service: 0, damage_note: v?.note.trim() ?? "" };
        }),
        p_returned_by: returnedBy.trim(),
        p_received_by: supervisor.trim(),
        p_notes: notes.trim(),
      });
      toast(report
        ? `Report re-issued (v${report.version + 1}) — ${displayName(returnedBy)} has been asked to confirm it`
        : `Return report issued — ${displayName(returnedBy)} has been asked to confirm it`);
      onClose();
      await onIssued();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't issue the report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="modal-backdrop open" role="presentation" {...backdropHandlers(onClose)}>
      <div class="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="eqReturnTitle" tabindex={-1} onClick={(e) => e.stopPropagation()}>
        <h3 id="eqReturnTitle">{report ? `Amend return report — ${booking.reference}` : `Return report — ${booking.reference}`}</h3>
        <div class="modal-date">
          {displayName(booking.on_behalf_of || booking.requester_name)} · expected {booking.return_date}
        </div>
        <p class="eq-modal-purpose">
          Check each item and declare what came back. Damaged units are set aside (not bookable) until an
          admin marks them repaired in Inventory. The holder receives the report by email and must confirm
          it in the app; the booking closes when they do (or when you close it without confirmation, with a note).
          {report?.status === "DISPUTED" && report.dispute_note && <> Dispute note: <b>“{report.dispute_note}”</b></>}
        </p>
        <div class="eq-return-lines">
          {lines.map((l) => {
            const v = st[l.id];
            if (!v) return null;
            const due = l.qty_approved ?? l.qty_requested;
            return (
              <div key={l.id} class="eq-return-line">
                <div class="eq-return-name"><b>{nameOf(l)}</b> — {due} out</div>
                <div class="eq-return-grid">
                  <label>Returned
                    <input type="number" min={0} max={due} value={v.returned}
                      onInput={(e) => upd(l.id, { returned: Number((e.target as HTMLInputElement).value) || 0 })} />
                  </label>
                  <label>Lost
                    <input type="number" min={0} max={due} value={v.lost}
                      onInput={(e) => upd(l.id, { lost: Number((e.target as HTMLInputElement).value) || 0 })} />
                  </label>
                  <label>Damaged (of returned)
                    <input type="number" min={0} max={v.returned} value={v.damaged}
                      onInput={(e) => upd(l.id, { damaged: Number((e.target as HTMLInputElement).value) || 0 })} />
                  </label>
                </div>
                {v.damaged > 0 && (
                  <label class="eq-field eq-field-wide">
                    <span>Damage description</span>
                    <input type="text" value={v.note} maxLength={200}
                      placeholder="e.g. cappuccio mancante"
                      onInput={(e) => upd(l.id, { note: (e.target as HTMLInputElement).value })} />
                  </label>
                )}
              </div>
            );
          })}
        </div>
        {problems.length > 0 && <div class="eq-problems">{problems[0]}</div>}
        <div class="eq-form-grid">
          <PersonSelect label="Returned by" value={returnedBy} onChange={setReturnedBy} />
          <PersonSelect label="General Affairs — received by" value={supervisor} onChange={setSupervisor} />
          <label class="eq-field eq-field-wide">
            <span>Notes for the holder <i>(optional, printed on the report)</i></span>
            <textarea value={notes} maxLength={400} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
          </label>
        </div>
        <div class="modal-actions">
          <button type="button" class="modal-secondary" onClick={onClose}>Close</button>
          <button type="button" class="modal-primary" disabled={busy || problems.length > 0} onClick={() => void save()}>
            {busy ? "Saving…" : report ? "Re-issue report" : "Issue report"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── admin: archive of stored PDFs ───────────────── */

export function ArchiveTab({
  bookings, reports, docs, me, onChanged, logEvent,
}: {
  bookings: EquipmentBooking[];
  reports: EquipmentReturnReport[];
  docs: EquipmentDocument[];
  me: string;
  onChanged: () => Promise<void>;
  logEvent: (row: Record<string, unknown>) => Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [toRemove, setToRemove] = useState<EquipmentDocument[] | null>(null);
  const demo = isDemoMode.value;

  const live = docs.filter((d) => d.kind === "return_report" && !d.archived_at)
    .sort((a, b) => a.file_name.localeCompare(b.file_name));
  const archived = docs.filter((d) => d.kind === "return_report" && d.archived_at);
  const bookingOf = (id: number): EquipmentBooking | undefined => bookings.find((b) => b.id === id);
  const missing = bookings.filter((b) =>
    b.status === "RETURNED"
    && reports.some((r) => r.booking_id === b.id && r.status === "CONFIRMED")
    && !docs.some((d) => d.booking_id === b.id && d.kind === "return_report"),
  );
  const liveBytes = live.reduce((a, d) => a + d.size_bytes, 0);
  const today = new Date().toISOString().slice(0, 10);

  const downloadAll = async (): Promise<void> => {
    if (demo) { toast("Demo mode — there are no stored files to download."); return; }
    if (live.length === 0) return;
    setBusy(true);
    try {
      const entries: Array<{ name: string; bytes: Uint8Array }> = [];
      for (const d of live) {
        const blob = await storageDownload("equipment-docs", d.path);
        entries.push({ name: d.file_name, bytes: new Uint8Array(await blob.arrayBuffer()) });
      }
      const { buildZip } = await import("./equipmentArchive");
      saveBytes(buildZip(entries), `equipment-return-reports-${today}.zip`, "application/zip");
      setToRemove(live);
      toast(`ZIP with ${live.length} report${live.length === 1 ? "" : "s"} downloaded — check it is saved, then remove the files from storage below`);
    } catch (err) {
      toast(err instanceof Error ? `Download failed — ${err.message}` : "Download failed");
    } finally {
      setBusy(false);
    }
  };

  const removeDownloaded = async (): Promise<void> => {
    if (!toRemove || toRemove.length === 0) return;
    if (!confirm(`Remove ${toRemove.length} PDF file${toRemove.length === 1 ? "" : "s"} from storage?\n\nOnly do this once the ZIP is safely saved. The reports stay in the register and can be regenerated; the files themselves are deleted.`)) return;
    setBusy(true);
    try {
      const paths = toRemove.map((d) => d.path);
      for (let i = 0; i < paths.length; i += 100) await storageDelete("equipment-docs", paths.slice(i, i + 100));
      const ids = toRemove.map((d) => d.id);
      for (let i = 0; i < ids.length; i += 100) {
        await restPatch("equipment_booking_documents", `id=in.(${ids.slice(i, i + 100).join(",")})`, {
          archived_at: nowIso(),
          archived_by: displayName(me),
        });
      }
      await logEvent({ event_type: "archived", note: `Archived offline ${ids.length} return report PDFs (${formatBytes(toRemove.reduce((a, d) => a + d.size_bytes, 0))})` });
      toast(`${ids.length} file${ids.length === 1 ? "" : "s"} removed from storage`);
      setToRemove(null);
      await onChanged();
    } catch (err) {
      toast(err instanceof Error ? `Removal stopped — ${err.message}` : "Removal failed");
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const generateMissing = async (): Promise<void> => {
    if (demo) { toast("Demo mode — nothing is generated."); return; }
    setBusy(true);
    let n = 0;
    try {
      for (const b of missing) {
        const r = reports.find((x) => x.booking_id === b.id);
        if (!r) continue;
        await storeReturnReportPdf(b, r, displayName(me));
        n++;
      }
      toast(`${n} PDF${n === 1 ? "" : "s"} generated and stored`);
      await onChanged();
    } catch (err) {
      toast(err instanceof Error ? `Stopped after ${n} — ${err.message}` : "Generation failed");
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="eq-card eq-archive">
      <div class="eq-section-title">Return report archive</div>
      <p class="eq-inventory-hint">
        Every confirmed return is stored as a small PDF next to its booking. Download them all as one ZIP —
        files are named <i>reference_date_holder_return-report.pdf</i> — and, once the ZIP is safely saved,
        remove the files from storage. The reports stay in the register and can be regenerated at any time.
      </p>
      <div class="eq-kpis">
        <span class="eq-kpi"><b>{live.length}</b> stored · {formatBytes(liveBytes)}</span>
        <span class="eq-kpi"><b>{archived.length}</b> archived offline</span>
        {missing.length > 0 && <span class="eq-kpi eq-kpi-warn"><b>{missing.length}</b> closed without a stored PDF</span>}
      </div>
      {live.length > 0 && (
        <div class="eq-scroll">
          <table class="eq-table" aria-label="Stored return reports">
            <thead><tr><th>Reference</th><th>Holder</th><th>Returned on</th><th class="num">Size</th><th>File</th></tr></thead>
            <tbody>
              {live.map((d) => {
                const b = bookingOf(d.booking_id);
                const r = reports.find((x) => x.booking_id === d.booking_id);
                return (
                  <tr key={d.id}>
                    <td><b>{b?.reference ?? d.booking_id}</b></td>
                    <td>{displayName(r?.returned_by || b?.requester_name || "")}</td>
                    <td>{r ? fmtStamp(r.declared_at) : d.created_at.slice(0, 10)}</td>
                    <td class="num">{formatBytes(d.size_bytes)}</td>
                    <td class="eq-notes-cell">{d.file_name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div class="eq-booking-actions eq-archive-actions">
        <button type="button" class="eq-action" disabled={busy || live.length === 0} onClick={() => void downloadAll()}>
          {busy ? "Working…" : live.length === 0 ? "Nothing to download" : `Download all as ZIP (${live.length} · ${formatBytes(liveBytes)})`}
        </button>
        {missing.length > 0 && (
          <button type="button" class="eq-action eq-action-quiet" disabled={busy} onClick={() => void generateMissing()}>
            Generate {missing.length} missing PDF{missing.length === 1 ? "" : "s"}
          </button>
        )}
      </div>
      {toRemove && toRemove.length > 0 && (
        <div class="eq-archive-confirm">
          <b>Step 2 of 2.</b> Open the ZIP and check the {toRemove.length} file{toRemove.length === 1 ? "" : "s"} are there.
          Then free the space:
          <button type="button" class="eq-action eq-action-danger" disabled={busy} onClick={() => void removeDownloaded()}>
            Remove {toRemove.length} file{toRemove.length === 1 ? "" : "s"} from storage
          </button>
          <button type="button" class="eq-action eq-action-quiet" disabled={busy} onClick={() => setToRemove(null)}>Keep them</button>
        </div>
      )}
    </div>
  );
}
